// ABOUTME: Resolves the effective WhatsApp application settings shown for a job.
// ABOUTME: Gives explicitly persisted link settings precedence over flow defaults.
export type WhatsappApplicationOptionsDraftByEditorKey = Record<string, string>;

type WhatsappApplicationOptionsNode = {
    editorKey: string;
    options: string[];
};

export type WhatsappApplicationOptionsEditorState<
    TFlow extends { nodes: WhatsappApplicationOptionsNode[] }
> = {
    flow: TFlow;
    optionsDraftByEditorKey: WhatsappApplicationOptionsDraftByEditorKey;
};

export const parseWhatsappApplicationOptionsDraft = (text: string) =>
    text
        .split(",")
        .map((option) => option.trim())
        .filter(Boolean);

export const formatWhatsappApplicationOptions = (options: string[]) =>
    options.join(", ");

export const buildWhatsappApplicationOptionsDraftByEditorKey = (
    nodes: WhatsappApplicationOptionsNode[]
): WhatsappApplicationOptionsDraftByEditorKey =>
    nodes.reduce<WhatsappApplicationOptionsDraftByEditorKey>(
        (drafts, node) => ({
            ...drafts,
            [node.editorKey]: formatWhatsappApplicationOptions(node.options)
        }),
        {}
    );

export const updateWhatsappApplicationOptionsDraft = <
    TFlow extends { nodes: WhatsappApplicationOptionsNode[] }
>(
    state: WhatsappApplicationOptionsEditorState<TFlow>,
    nodeEditorKey: string,
    text: string
): WhatsappApplicationOptionsEditorState<TFlow> => {
    const options = parseWhatsappApplicationOptionsDraft(text);

    return {
        flow: {
            ...state.flow,
            nodes: state.flow.nodes.map((node) =>
                node.editorKey === nodeEditorKey ? { ...node, options } : node
            )
        } as TFlow,
        optionsDraftByEditorKey: {
            ...state.optionsDraftByEditorKey,
            [nodeEditorKey]: text
        }
    };
};

export const canonicalizeWhatsappApplicationOptionsDraft = <
    TFlow extends { nodes: WhatsappApplicationOptionsNode[] }
>(
    state: WhatsappApplicationOptionsEditorState<TFlow>,
    nodeEditorKey: string
): WhatsappApplicationOptionsEditorState<TFlow> => {
    const options =
        state.flow.nodes.find((node) => node.editorKey === nodeEditorKey)
            ?.options || [];

    return {
        ...state,
        optionsDraftByEditorKey: {
            ...state.optionsDraftByEditorKey,
            [nodeEditorKey]: formatWhatsappApplicationOptions(options)
        }
    };
};

export const removeWhatsappApplicationOptionsNode = <
    TFlow extends { nodes: WhatsappApplicationOptionsNode[] }
>(
    state: WhatsappApplicationOptionsEditorState<TFlow>,
    nodeEditorKey: string
): WhatsappApplicationOptionsEditorState<TFlow> => {
    const optionsDraftByEditorKey = {
        ...state.optionsDraftByEditorKey
    };
    delete optionsDraftByEditorKey[nodeEditorKey];

    return {
        flow: {
            ...state.flow,
            nodes: state.flow.nodes.filter(
                (node) => node.editorKey !== nodeEditorKey
            )
        } as TFlow,
        optionsDraftByEditorKey
    };
};

export const applyWhatsappApplicationLinkSettings = <
    TFlow extends Record<string, any>
>(
    flow: TFlow,
    link?: Record<string, any> | null
): TFlow => {
    const result: Record<string, any> = {
        ...flow
    };

    if (link) {
        result.collectScreeningQuestionnaire =
            link.collectScreeningQuestionnaire === true;
    }

    if (
        link &&
        Object.prototype.hasOwnProperty.call(
            link,
            "requireApplicationFormRequiredFields"
        )
    ) {
        result.requireApplicationFormRequiredFields =
            link.requireApplicationFormRequiredFields === true;
    }

    return result as TFlow;
};

type WhatsappPreRegistrationField =
    | "nodeId"
    | "nodeReference"
    | "answerKey"
    | "options"
    | "conditionValue";

const whatsappPreRegistrationNodeTranslationKeys: Record<string, string> = {
    consent_1: "whatsappPreRegistrationNodeConsent",
    candidate_name: "whatsappPreRegistrationNodeCandidateName",
    date_of_birth: "whatsappPreRegistrationNodeDateOfBirth",
    underage_message: "whatsappPreRegistrationNodeUnderageMessage",
    underage_end: "whatsappPreRegistrationNodeUnderageEnd",
    position: "whatsappPreRegistrationNodePosition",
    availability: "whatsappPreRegistrationNodeAvailability",
    morning_message: "whatsappPreRegistrationNodeMorningMessage",
    morning_end: "whatsappPreRegistrationNodeMorningEnd",
    resume: "whatsappPreRegistrationNodeResume",
    eligible_message: "whatsappPreRegistrationNodeEligibleMessage",
    eligible_end: "whatsappPreRegistrationNodeEligibleEnd"
};

const whatsappPreRegistrationAnswerTranslationKeys: Record<string, string> = {
    candidateName: "whatsappPreRegistrationAnswerCandidateName",
    dateOfBirth: "whatsappPreRegistrationAnswerDateOfBirth",
    position: "whatsappPreRegistrationAnswerPosition",
    availability: "whatsappPreRegistrationAnswerAvailability",
    resume: "whatsappPreRegistrationAnswerResume"
};

const whatsappPreRegistrationAvailabilityTranslationKeys: Record<
    string,
    string
> = {
    MORNING_ONLY: "whatsappPreRegistrationAvailabilityMorningOnly",
    OTHER_SHIFTS: "whatsappPreRegistrationAvailabilityOtherShifts"
};

const whatsappPreRegistrationAvailabilityAliases: Record<string, string> = {
    MORNING_ONLY: "MORNING_ONLY",
    "MORNING ONLY": "MORNING_ONLY",
    MANHA: "MORNING_ONLY",
    "SOMENTE MANHA": "MORNING_ONLY",
    OTHER_SHIFTS: "OTHER_SHIFTS",
    "OTHER SHIFTS": "OTHER_SHIFTS",
    TARDE: "OTHER_SHIFTS",
    "OUTROS TURNOS": "OTHER_SHIFTS",
    "TARDE E NOITE": "OTHER_SHIFTS"
};

export const normalizeWhatsappPreRegistrationAvailabilityValue = (
    value: unknown
) => {
    const normalized = String(value ?? "")
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();
    return whatsappPreRegistrationAvailabilityAliases[normalized] ||
        String(value ?? "").trim();
};

export const getWhatsappPreRegistrationFieldPresentation = ({
    scope,
    field,
    value,
    answerKey
}: {
    scope: string;
    field: WhatsappPreRegistrationField;
    value: string | string[];
    answerKey?: string;
}) => {
    if (scope !== "PRE_REGISTRATION") {
        return null;
    }

    if (field === "nodeId" || field === "nodeReference") {
        const translationKey =
            whatsappPreRegistrationNodeTranslationKeys[String(value)];
        return translationKey
            ? { translationKeys: [translationKey], readonly: true as const }
            : null;
    }

    if (field === "answerKey") {
        const translationKey =
            whatsappPreRegistrationAnswerTranslationKeys[String(value)];
        return translationKey
            ? { translationKeys: [translationKey], readonly: true as const }
            : null;
    }

    if (field === "options") {
        if (
            answerKey !== "availability" ||
            !Array.isArray(value) ||
            value.length !== 2 ||
            value[0] !== "MORNING_ONLY" ||
            value[1] !== "OTHER_SHIFTS"
        ) {
            return null;
        }

        return {
            translationKeys: value.map(
                (option) =>
                    whatsappPreRegistrationAvailabilityTranslationKeys[option]
            ),
            readonly: true as const
        };
    }

    if (
        field === "conditionValue" &&
        answerKey === "availability" &&
        normalizeWhatsappPreRegistrationAvailabilityValue(value) ===
            "OTHER_SHIFTS"
    ) {
        return {
            translationKeys: [
                whatsappPreRegistrationAvailabilityTranslationKeys.OTHER_SHIFTS
            ],
            readonly: true as const
        };
    }

    return null;
};
